import { Activity, EarlyBirdBooking } from '../../types/GrooverTypes'
import { DiscountPricingConfig } from '../../types/DiscountTypes'

type TakenSeatsMap = {
  byConfig: Record<string, number> // seats taken per pricing config ID
  byType: Record<string, number> // total seats taken per type (for regular seats calculation)
}

/**
 * Calculates how many seats are taken for each pricing config based on existing registrations
 */
const calculateTakenSeats = (registrations: EarlyBirdBooking[]): TakenSeatsMap => {
  const takenSeats: TakenSeatsMap = {
    byConfig: {},
    byType: {}
  }

  registrations
    .filter((reg) => reg.status === 'confirmed' || reg.status === 'pending')
    .forEach((reg) => {
      // Determine seats needed based on registration type
      // Single registrations take 1 seat, couple registrations take 2 seats
      const seatsToTake = reg.registrationType === 'single' ? 1 : 2

      // Track seats by type for regular seats calculation
      takenSeats.byType[reg.registrationType] = (takenSeats.byType[reg.registrationType] || 0) + seatsToTake

      // If registration uses a specific pricing config, track those seats separately
      if (reg.pricingConfigId) {
        takenSeats.byConfig[reg.pricingConfigId] = (takenSeats.byConfig[reg.pricingConfigId] || 0) + seatsToTake
      }
    })

  return takenSeats
}

/**
 * Applies registration stats to pricing configs and returns updated configs with actual seats available
 */
export const applyRegistrationStats = (activity: Activity, registrations: EarlyBirdBooking[]): Activity => {
  if (!activity.discountConfig) return activity

  const takenSeats = calculateTakenSeats(registrations.filter((reg) => reg.activityId === activity.id))

  const updatedPricingConfigs = activity.discountConfig.pricingConfigs.map((config) => ({
    ...config,
    seatsAvailable: Math.max(0, config.seatsAvailable - (takenSeats.byConfig[config.id] || 0))
  }))

  return {
    ...activity,
    discountConfig: {
      ...activity.discountConfig,
      pricingConfigs: updatedPricingConfigs,
      // Update maxRegularSeats by subtracting all taken seats
      maxRegularSeats: Math.max(
        0,
        activity.discountConfig.maxRegularSeats -
          Object.values(takenSeats.byType).reduce((sum, seats) => sum + seats, 0)
      )
    }
  }
}

/**
 * Gets the best available price for a given registration type and region
 * Returns undefined if no seats are available (including regular seats)
 */
export const getBestAvailablePrice = (
  activity: Activity,
  registrationType: DiscountPricingConfig['registrationType'],
  region: string
): number | undefined => {
  if (!activity.discountConfig) return undefined

  // Determine seats needed based on registration type
  const seatsNeeded = registrationType === 'couple' ? 2 : 1

  // Find cheapest available pricing config
  const selectedConfig = findSelectedPricingConfig(activity, registrationType, undefined, region)
  if (selectedConfig) {
    return selectedConfig.price
  }

  // If no promo seats available, check regular seats
  // But only if the registration type is a basic type (single/couple), not a specific type
  if (activity.discountConfig.maxRegularSeats >= seatsNeeded) {
    return activity.price // Use regular price
  }

  return undefined // No seats available
}

/**
 * Finds the pricing config that was used to determine the price
 * Returns undefined if the price is the regular price or no price is available
 * If price is undefined, returns the cheapest available config
 */
export const findSelectedPricingConfig = (
  activity: Activity,
  registrationType: string,
  price: number | undefined,
  region?: string
): DiscountPricingConfig | undefined => {
  if (!activity.discountConfig) return undefined

  // Determine seats needed based on registration type
  const seatsNeeded = registrationType.startsWith('couple') ? 2 : 1

  // Determine which region to use
  let targetRegion = region
  if (!targetRegion && activity.regions && activity.regions.length === 1) {
    // If activity has only one region, use that one
    targetRegion = activity.regions[0]
  }

  // Filter configs by type, seats availability, and region (if specified)
  const filterConfigs = (configs: DiscountPricingConfig[]) => {
    return configs.filter((config) => {
      const typeMatch = config.registrationType === registrationType
      const seatsMatch = config.seatsAvailable >= seatsNeeded
      const regionMatch = !targetRegion || config.region === targetRegion || config.region === 'All'
      return typeMatch && seatsMatch && regionMatch
    })
  }

  // If price is undefined, find cheapest available config
  if (price === undefined) {
    const availableConfigs = filterConfigs([...activity.discountConfig.pricingConfigs])
    return availableConfigs.sort((a, b) => a.price - b.price)[0]
  }

  // If price matches regular price, no config was used
  if (price === activity.price) return undefined

  // Find config matching the given price
  const availableConfigs = filterConfigs(activity.discountConfig.pricingConfigs)
  return availableConfigs.find((config) => config.price === price)
}
